import { useTranslation } from "@refinedev/core";

import { Button, CircularProgress, Paper, Stack, Typography, TypographyProps } from "@mui/material";

interface EditableTableCardProps {
  title: string;
  onEdit: () => void;
  onDone?: () => void;
  children: React.ReactNode;
  isEditing: boolean;
  setIsEditing: React.Dispatch<React.SetStateAction<boolean>>;
  titleVariant?: TypographyProps["variant"];
  hideEditButton?: boolean;
  header?: React.ReactNode;
  disableContentPadding?: boolean;
  loading?: boolean;
}

export const EditableTableCard: React.FC<EditableTableCardProps> = ({
  title,
  onEdit,
  onDone,
  children,
  isEditing,
  setIsEditing,
  titleVariant = "h4",
  hideEditButton = false,
  header,
  disableContentPadding = false,
  loading = false,
}: EditableTableCardProps) => {
  const { translate } = useTranslation();

  // Handle toggling edit mode
  const handleToggleEdit = () => {
    if (isEditing) {
      // If currently editing, finish editing
      if (onDone) {
        onDone();
      } else {
        // if no onDone function is provided, just set editing to false
        setIsEditing(false);
      }
    } else {
      // Enter edit mode
      onEdit();
      setIsEditing(true);
    }
  };

  return (
    <Paper elevation={1} variant="outlined">
      {loading ? (
        <Stack alignItems="center" py={8}>
          <CircularProgress />
        </Stack>
      ) : (
        <>
          {header}
          <Stack spacing={2} p={disableContentPadding ? 0 : 3}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant={titleVariant}>{title}</Typography>
              {!hideEditButton && (
                <Button variant="contained" size="small" onClick={handleToggleEdit}>
                  {isEditing ? translate("buttons.done") : translate("buttons.edit")}
                </Button>
              )}
            </Stack>
            <Stack width="100%">{children}</Stack>
          </Stack>
        </>
      )}
    </Paper>
  );
};
